/**
 * Floating help / support widget for pump users.
 * Submit issues, browse FAQ autocomplete, view ticket status and admin replies.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import swal from 'sweetalert';
import {
  createSupportTicket,
  downloadSupportAttachment,
  fetchMySupportTickets,
  fetchSupportMeta,
  fetchSupportTicketDetail,
  replySupportTicket,
  saveBlobAsFile,
  searchSupportFaq,
} from './supportApi';
import './help-support.css';

const DEFAULT_FORM = {
  subject: '',
  description: '',
  category: 'complaint',
  priority: 'medium',
  faq_id: null,
};

function StatusBadge({ status, label }) {
  return (
    <span className={`help-status-badge help-status-${status}`}>
      {label || status}
    </span>
  );
}

function formatBytes(size) {
  const n = Number(size || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function HelpSupportWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('new');
  const [meta, setMeta] = useState({ categories: [], priorities: [] });
  const [form, setForm] = useState(DEFAULT_FORM);
  const [files, setFiles] = useState([]);
  const [faqQuery, setFaqQuery] = useState('');
  const [faqResults, setFaqResults] = useState([]);
  const [faqAnswer, setFaqAnswer] = useState('');
  const [showFaqDropdown, setShowFaqDropdown] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const faqTimer = useRef(null);
  const faqWrapRef = useRef(null);

  const loadTickets = useCallback(() => {
    fetchMySupportTickets()
      .then((res) => {
        setTickets(res.tickets || []);
        setOpenCount(res.open_count || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (open) {
      fetchSupportMeta().then(setMeta).catch(() => {});
      loadTickets();
    }
  }, [open, loadTickets]);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (faqWrapRef.current && !faqWrapRef.current.contains(e.target)) {
        setShowFaqDropdown(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function onFaqInput(value) {
    setFaqQuery(value);
    setForm((f) => ({ ...f, subject: value, faq_id: null }));
    setFaqAnswer('');
    clearTimeout(faqTimer.current);
    if (value.trim().length < 2) {
      setFaqResults([]);
      setShowFaqDropdown(false);
      return;
    }
    faqTimer.current = setTimeout(() => {
      searchSupportFaq(value.trim())
        .then((res) => {
          setFaqResults(res.faqs || []);
          setShowFaqDropdown((res.faqs || []).length > 0);
        })
        .catch(() => setFaqResults([]));
    }, 250);
  }

  function selectFaq(faq) {
    setFaqQuery(faq.question);
    setForm((f) => ({
      ...f,
      subject: faq.question,
      faq_id: faq.id,
      category: faq.category || f.category,
    }));
    setFaqAnswer(faq.answer || '');
    setShowFaqDropdown(false);
  }

  function onFilesChange(e) {
    const selected = Array.from(e.target.files || []);
    const maxFiles = meta?.attachments?.max_files || 5;
    if (selected.length > maxFiles) {
      swal('Too many files', `You can upload up to ${maxFiles} files.`, 'warning');
      e.target.value = '';
      return;
    }
    setFiles(selected);
  }

  function submitTicket(e) {
    e.preventDefault();
    setSubmitting(true);
    createSupportTicket(form, files)
      .then((res) => {
        swal('Submitted', res.message || 'Your request was sent', 'success');
        setForm(DEFAULT_FORM);
        setFiles([]);
        setFaqQuery('');
        setFaqAnswer('');
        loadTickets();
        setTab('mine');
        if (res.ticket) {
          setSelectedTicket(res.ticket);
        }
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setSubmitting(false));
  }

  function openTicket(ticketId) {
    fetchSupportTicketDetail(ticketId)
      .then((res) => {
        if (res.ticket) setSelectedTicket(res.ticket);
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function downloadAttachment(attachment) {
    if (!selectedTicket) return;
    downloadSupportAttachment(selectedTicket.id, attachment.id, false)
      .then(({ blob, filename }) => saveBlobAsFile(blob, filename || attachment.original_name))
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function sendReply(e) {
    e.preventDefault();
    if (!selectedTicket || replyText.trim().length < 3) return;
    setSubmitting(true);
    replySupportTicket(selectedTicket.id, replyText.trim())
      .then((res) => {
        setReplyText('');
        setSelectedTicket(res.ticket || selectedTicket);
        loadTickets();
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setSubmitting(false));
  }

  function closePanel() {
    setOpen(false);
    setSelectedTicket(null);
    setTab('new');
  }

  return (
    <>
      <button
        type="button"
        className="help-support-trigger nav-link"
        title="Request help"
        onClick={() => setOpen(true)}
        aria-label="Request help"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="#495057" viewBox="0 0 16 16">
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
          <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.583 0-1.009.388-1.009.94z"/>
        </svg>
        {openCount > 0 && (
          <span className="badge bg-primary help-support-badge">{openCount}</span>
        )}
      </button>

      {open && (
        <div className="help-support-overlay" onClick={closePanel}>
          <div className="help-support-panel" onClick={(e) => e.stopPropagation()}>
            <div className="help-support-header">
              <div>
                <h5 className="mb-0">Help &amp; Support</h5>
                <small className="text-muted">Describe your issue — our team will respond here</small>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closePanel}>Close</button>
            </div>

            <div className="help-support-tabs">
              <button type="button" className={tab === 'new' ? 'active' : ''} onClick={() => { setTab('new'); setSelectedTicket(null); }}>
                New request
              </button>
              <button type="button" className={tab === 'mine' ? 'active' : ''} onClick={() => { setTab('mine'); setSelectedTicket(null); loadTickets(); }}>
                My requests {openCount > 0 ? `(${openCount})` : ''}
              </button>
            </div>

            <div className="help-support-body">
              {tab === 'new' && (
                <form onSubmit={submitTicket}>
                  <div className="mb-3 help-faq-suggest" ref={faqWrapRef}>
                    <label className="form-label small">Subject — type to search FAQ</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={faqQuery}
                      onChange={(e) => onFaqInput(e.target.value)}
                      onFocus={() => faqResults.length > 0 && setShowFaqDropdown(true)}
                      placeholder="e.g. day closing, balance sheet mismatch…"
                      required
                      minLength={5}
                    />
                    {showFaqDropdown && faqResults.length > 0 && (
                      <div className="help-faq-dropdown">
                        {faqResults.map((faq) => (
                          <button key={faq.id} type="button" onClick={() => selectFaq(faq)}>
                            {faq.question}
                          </button>
                        ))}
                      </div>
                    )}
                    {faqAnswer && (
                      <div className="help-faq-answer">
                        <strong>Suggested answer:</strong>
                        <p className="mb-0 mt-1">{faqAnswer}</p>
                        <small className="text-muted">Still need help? Complete the form below.</small>
                      </div>
                    )}
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <label className="form-label small">Category</label>
                      <select
                        className="form-select form-select-sm"
                        value={form.category}
                        onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                        required
                      >
                        {(meta.categories || []).map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Priority</label>
                      <select
                        className="form-select form-select-sm"
                        value={form.priority}
                        onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                        required
                      >
                        {(meta.priorities || []).map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small">Describe your issue</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={4}
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Include dates, screen names, error messages if any…"
                      required
                      minLength={10}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label small">
                      Attachments (optional) — screenshots or PDF, max {meta?.attachments?.max_files || 5}
                    </label>
                    <input
                      type="file"
                      className="form-control form-control-sm"
                      accept="image/*,.pdf,application/pdf"
                      multiple
                      onChange={onFilesChange}
                    />
                    {files.length > 0 && (
                      <ul className="small text-muted mt-1 mb-0">
                        {files.map((f) => (
                          <li key={`${f.name}-${f.size}`}>{f.name} ({formatBytes(f.size)})</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                    {submitting ? 'Sending…' : 'Submit request'}
                  </button>
                </form>
              )}

              {tab === 'mine' && !selectedTicket && (
                <>
                  {tickets.length === 0 ? (
                    <p className="text-muted">No support requests yet.</p>
                  ) : (
                    <ul className="help-ticket-list">
                      {tickets.map((t) => (
                        <li key={t.id} onClick={() => openTicket(t.id)}>
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div>
                              <strong>{t.ticket_no}</strong>
                              <span className={`ms-2 help-priority-${t.priority}`}>{t.priority_label}</span>
                              <div className="small mt-1">{t.subject}</div>
                            </div>
                            <StatusBadge status={t.status} label={t.status_label} />
                          </div>
                          <div className="small text-muted mt-1">
                            {t.category_label} · {t.updated_at}
                            {t.has_admin_reply && <span className="text-primary ms-2">New reply</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {tab === 'mine' && selectedTicket && (
                <div>
                  <button type="button" className="btn btn-link btn-sm p-0 mb-2" onClick={() => setSelectedTicket(null)}>
                    ← Back to list
                  </button>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="mb-1">{selectedTicket.ticket_no} — {selectedTicket.subject}</h6>
                      <small className="text-muted">{selectedTicket.category_label}</small>
                    </div>
                    <StatusBadge status={selectedTicket.status} label={selectedTicket.status_label} />
                  </div>

                  <div className="help-message-thread mb-3">
                    {(selectedTicket.messages || []).map((m) => (
                      <div key={m.id} className={`help-message-bubble ${m.author_type}`}>
                        <div className="bubble-inner">{m.message}</div>
                        <div className="help-message-meta">
                          {m.author_name} · {m.created_at}
                        </div>
                      </div>
                    ))}
                  </div>

                  {(selectedTicket.attachments || []).length > 0 && (
                    <div className="mb-3">
                      <div className="small fw-semibold mb-1">Attachments</div>
                      <ul className="list-unstyled small mb-0">
                        {selectedTicket.attachments.map((a) => (
                          <li key={a.id} className="d-flex justify-content-between align-items-center border rounded px-2 py-1 mb-1">
                            <span>{a.original_name} <span className="text-muted">({formatBytes(a.size)})</span></span>
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => downloadAttachment(a)}>
                              Download
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(selectedTicket.status_history || []).length > 0 && (
                    <div className="mb-3">
                      <div className="small fw-semibold mb-1">Status history</div>
                      <ul className="small text-muted mb-0">
                        {selectedTicket.status_history.map((h) => (
                          <li key={h.id}>
                            {h.created_at}: {h.from_status_label || '—'} → {h.to_status_label}
                            {h.changed_by_name ? ` (${h.changed_by_name})` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!['closed', 'resolved'].includes(selectedTicket.status) && (
                    <form onSubmit={sendReply}>
                      <textarea
                        className="form-control form-control-sm mb-2"
                        rows={2}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Add a follow-up message…"
                        minLength={3}
                      />
                      <button type="submit" className="btn btn-outline-primary btn-sm" disabled={submitting}>
                        Send follow-up
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
